import React, { useEffect } from 'react';
import { TreeTable, TreeTableSortEvent } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { useState, useCallback, useMemo } from 'react';
import { TextOverflowTooltip } from 'src/common';
import { Header } from './comps/Header';
import { OrgAvatar, SortIcon, Tdm } from './comps';
import { GTColumnSelectAnchor } from 'src/components/GenericTable/comps';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { get, has, omit } from 'lodash';
import { ColumnHeader, Container, TableContainer } from './styled';
import { MemberAccess } from 'src/graphql/_generated';
import { TableIds } from 'src/api/types/misc';
import { buildTreeFromFlat, convertToTreeTableFormat, FlatOrgData, FlatOrgTreeTableNode } from 'src/utils/treeNode';
import {
  getBillingTypeLabel,
  getBusinessTypeLabel,
  getEnvironmentTypeLabel,
  getMemberAccessLabel,
  getOrganisationTypeLabel,
  getPlanTypeLabel,
  getServiceModelLabel,
} from 'src/api/types';

interface OrgTableProps {
  data: FlatOrgData[];
  from?: string;
  showArchived: boolean;
}

const OrgTable: React.FC<OrgTableProps> = ({ data, from, showArchived }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useLocalStorage<string>(`${TableIds.rootHerarchy}-sort-field`, '');
  const [sortOrder, setSortOrder] = useLocalStorage<0 | 1 | -1 | null | undefined>(
    `${TableIds.rootHerarchy}-sort-order`,
    1,
  );
  const [visibleColumns, setVisibleColumns] = useLocalStorage<string[]>(`${TableIds.rootHerarchy}-visible-columns`, [
    'account-type',
    'environment-type',
    'access',
    'workspace-restriction',
  ]);
  const treeData = useMemo(() => buildTreeFromFlat(data, from, showArchived), [data, from, showArchived]);
  const treeTableData = useMemo(() => convertToTreeTableFormat(treeData), [treeData]);

  const [expandedKeys, setExpandedKeys] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (treeTableData.length > 0) {
      setExpandedKeys({ [treeTableData[0].key]: true });
    }
  }, [treeTableData]);

  const customSortFunction = useCallback(
    (data: FlatOrgTreeTableNode[]): FlatOrgTreeTableNode[] => {
      return [...data].sort((a, b) => {
        if (a.data.recordType !== b.data.recordType) {
          return a.data.recordType === 'W' ? -1 : 1;
        }

        if (sortField && sortOrder !== 0 && sortOrder !== null && sortOrder !== undefined) {
          let valueA = get(a.data, sortField);
          let valueB = get(b.data, sortField);

          if (valueA === undefined) return 1;
          if (valueB === undefined) return -1;

          if (typeof valueA === 'string' && typeof valueB === 'string') {
            return sortOrder * valueA.localeCompare(valueB);
          }

          return sortOrder * (valueA - valueB);
        }

        return 0;
      });
    },
    [sortField, sortOrder],
  );

  const applyCustomSort = useCallback(
    (dataArray: FlatOrgTreeTableNode[]): FlatOrgTreeTableNode[] => {
      const sortedData = customSortFunction(dataArray);

      return sortedData.map((node) => {
        if (node.children && node.children.length > 0) {
          return { ...node, children: applyCustomSort(node.children) };
        }
        return node;
      });
    },
    [customSortFunction],
  );

  const filterData = useCallback((data: FlatOrgTreeTableNode[], term: string): FlatOrgTreeTableNode[] => {
    if (!term.trim()) return JSON.parse(JSON.stringify(data));

    return data
      .filter((node) => {
        const matchesSearch =
          node.data.recordType === 'O' &&
          (node.data.name.toLowerCase().includes(term.toLowerCase()) ||
            node.data.domain.toLowerCase().includes(term.toLowerCase()) ||
            node.data.id.toLowerCase().includes(term.toLowerCase()) ||
            node.data.primaryContact.toLowerCase().includes(term.toLowerCase()));

        if (matchesSearch) return true;

        if (node.children && node.children.length > 0) {
          const filteredChildren = filterData(node.children, term);
          if (filteredChildren.length) {
            return true;
          }
        }

        return false;
      })
      .map((node) => {
        if (node.children && node.children.length > 0) {
          if (
            node.data.recordType === 'O' &&
            (node.data.name.toLowerCase().includes(term.toLowerCase()) ||
              node.data.domain.toLowerCase().includes(term.toLowerCase()) ||
              node.data.id.toLowerCase().includes(term.toLowerCase()) ||
              node.data.primaryContact.toLowerCase().includes(term.toLowerCase()))
          ) {
            return {
              ...node,
              children: JSON.parse(JSON.stringify(node.children)),
            };
          }

          const filteredChildren = filterData(node.children, term);
          return {
            ...node,
            children: filteredChildren,
          };
        }
        return node;
      });
  }, []);

  const onSort = (event: TreeTableSortEvent) => {
    if (sortField === event.sortField) {
      if (sortOrder === 1) {
        setSortOrder(-1);
      } else if (sortOrder === -1) {
        setSortField('');
        setSortOrder(0);
      } else {
        setSortField(event.sortField);
        setSortOrder(1);
      }
    } else {
      setSortField(event.sortField);
      setSortOrder(1);
    }
  };

  const filteredData = useMemo(() => {
    const filtered = filterData(treeTableData, searchTerm);
    return applyCustomSort(filtered);
  }, [filterData, treeTableData, searchTerm, applyCustomSort]);

  const columnSelectAnchor = useMemo(
    () => (
      <GTColumnSelectAnchor
        options={[
          { key: 'account-type', title: 'Account Type' },
          { key: 'organisation-type', title: 'Organisation Type' },
          { key: 'environment-type', title: 'Environment Type' },
          { key: 'service-model', title: 'Service Model' },
          { key: 'business-type', title: 'Business Type' },
          { key: 'billing-type', title: 'Billing Type' },
          { key: 'pricing-plan', title: 'Pricing Plan' },
          { key: 'access', title: 'Access' },
          { key: 'workspace-restriction', title: 'Workspace Restriction' },
          { key: 'domain', title: 'Domain' },
          { key: 'primary-contact', title: 'Primary Contact' },
        ]}
        value={visibleColumns}
        defaultOptions={['account-type', 'environment-type', 'access', 'workspace-restriction']}
        onChange={setVisibleColumns}
        tableId={TableIds.rootHerarchy}
      />
    ),
    [visibleColumns, setVisibleColumns],
  );

  const getColumnClassName = (column: string) => {
    return visibleColumns.includes(column) ? '' : 'c-hidden';
  };

  type NodeData = { data: FlatOrgTreeTableNode['data']; rowIndex: number };

  return (
    <Container>
      <TableContainer>
        <TreeTable
          value={filteredData}
          header={<Header onChange={setSearchTerm} />}
          onSort={onSort}
          filterMode="strict"
          sortOrder={sortOrder}
          expandedKeys={expandedKeys}
          onToggle={(e) => setExpandedKeys(e.value)}
          rowClassName={({ children }) => ({ expandable: children?.length ? true : false })}
          onRowClick={(e) => {
            if (e.node.children) {
              const nodeKey = e.node.key as string;
              const newExpandedKeys = has(expandedKeys, nodeKey)
                ? omit(expandedKeys, nodeKey)
                : { ...expandedKeys, [nodeKey]: true };
              setExpandedKeys(newExpandedKeys);
            }
          }}
          sortIcon={() => null}
        >
          {/* Name (O,W) */}
          <Column
            field="name"
            header={
              <ColumnHeader>
                <span>Name</span>
                <SortIcon sortOrder={sortField === 'name' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            expander
            sortable
            className="c-name"
            body={({ data }: NodeData) => {
              const isWorkspace = data.recordType === 'W';
              const allowedAccess = [MemberAccess.Allowed];

              return (
                <OrgAvatar
                  id={data.id}
                  name={data.name}
                  imgUrl={null}
                  gradientBlue={isWorkspace}
                  padlock={isWorkspace && !allowedAccess.includes(data.member?.access!)}
                  canAccess={data.actionsOnThisOrgUnit?.canAccess}
                />
              );
            }}
          />

          {/* Account Type, (O,W) */}
          <Column
            field="account-type"
            header={
              <ColumnHeader>
                <span>Account Type</span>
                <SortIcon sortOrder={sortField === 'account-type' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('account-type')}
            body={({ data }: NodeData) => {
              const archivedLabel = data.isArchived ? ' (Archived)' : '';
              return data.recordType === 'O' ? `Organisation${archivedLabel}` : `Workspace${archivedLabel}`;
            }}
          />

          {/* Organisation Type (O) */}
          <Column
            field="organisation-type"
            header={
              <ColumnHeader>
                <span>Organisation Type</span>
                <SortIcon sortOrder={sortField === 'organisation-type' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('organisation-type')}
            body={({ data }: NodeData) => {
              if (data.recordType === 'O') {
                return <TextOverflowTooltip>{getOrganisationTypeLabel(data.organisationType)}</TextOverflowTooltip>;
              }
              return null;
            }}
          />

          {/* Environment Type (O,W) */}
          <Column
            field="environment-type"
            header={
              <ColumnHeader>
                <span>Environment Type</span>
                <SortIcon sortOrder={sortField === 'environment-type' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('environment-type')}
            body={({ data }: NodeData) => (
              <TextOverflowTooltip>{data.plan || getEnvironmentTypeLabel(data.environmentType)}</TextOverflowTooltip>
            )}
          />

          {/* Service Model (O) */}
          <Column
            field="service-model"
            header={
              <ColumnHeader>
                <span>Service Model</span>
                <SortIcon sortOrder={sortField === 'service-model' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('service-model')}
            body={({ data }: NodeData) => {
              if (data.recordType === 'O') {
                return <TextOverflowTooltip>{getServiceModelLabel(data.serviceModel)}</TextOverflowTooltip>;
              }
              return null;
            }}
          />

          {/* Business Type (O) */}
          <Column
            field="business-type"
            header={
              <ColumnHeader>
                <span>Business Type</span>
                <SortIcon sortOrder={sortField === 'business-type' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('business-type')}
            body={({ data }: NodeData) => {
              if (data.recordType === 'O') {
                return <TextOverflowTooltip>{getBusinessTypeLabel(data.businessType)}</TextOverflowTooltip>;
              }
              return null;
            }}
          />

          {/* Billing Type (O) */}
          <Column
            field="billing-type"
            header={
              <ColumnHeader>
                <span>Billing Type</span>
                <SortIcon sortOrder={sortField === 'billing-type' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('billing-type')}
            body={({ data }: NodeData) => {
              if (data.recordType === 'O') {
                return <TextOverflowTooltip>{getBillingTypeLabel(data.billingType)}</TextOverflowTooltip>;
              }
              return null;
            }}
          />

          {/* Pricing Plan (O) */}
          <Column
            field="pricing-plan"
            header={
              <ColumnHeader>
                <span>Pricing Plan</span>
                <SortIcon sortOrder={sortField === 'pricing-plan' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('pricing-plan')}
            body={({ data }: NodeData) => {
              if (data.recordType === 'O') {
                return <TextOverflowTooltip>{getPlanTypeLabel(data.planType)}</TextOverflowTooltip>;
              }
              return null;
            }}
          />

          {/* Access (W) */}
          <Column
            field="access"
            header={
              <ColumnHeader>
                <span>Access</span>
                <SortIcon sortOrder={sortField === 'access' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('access')}
            body={({ data }: NodeData) => {
              if (data.recordType === 'W') {
                return <TextOverflowTooltip>{getMemberAccessLabel(data.member?.access)}</TextOverflowTooltip>;
              }
              return null;
            }}
          />

          {/* Workspace Restriction (W) */}
          <Column
            field="workspace-restriction"
            header={
              <ColumnHeader>
                <span>Workspace Restriction</span>
                <SortIcon sortOrder={sortField === 'workspace-restriction' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('workspace-restriction')}
            body={({ data }: NodeData) => {
              if (data.recordType === 'W') {
                return <TextOverflowTooltip>{data.isRestricted ? 'Restricted' : 'Open'}</TextOverflowTooltip>;
              }
              return null;
            }}
          />

          {/* Domain (O,W) */}
          <Column
            field="domain"
            header={
              <ColumnHeader>
                <span>Domain</span>
                <SortIcon sortOrder={sortField === 'domain' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('domain')}
            body={({ data }: NodeData) => <TextOverflowTooltip>{data.domain?.replace(/^@/, '')}</TextOverflowTooltip>}
          />

          {/* Primary Contact (O,W) */}
          <Column
            field="primary-contact"
            header={
              <ColumnHeader>
                <span>Primary Contact</span>
                <SortIcon sortOrder={sortField === 'primary-contact' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('primary-contact')}
            body={({ data }: NodeData) => <TextOverflowTooltip>{data.primaryContact}</TextOverflowTooltip>}
          />

          {/* TDM */}
          <Column
            field=""
            header={columnSelectAnchor}
            className="c-tdm"
            body={({ data, rowIndex }: NodeData) => <Tdm data={data} rowIndex={rowIndex} />}
          />
        </TreeTable>
      </TableContainer>
    </Container>
  );
};

export default OrgTable;
